import React from 'react';
import Image from 'next/image';
import { Block, BLOCKS, INLINES, Inline } from '@contentful/rich-text-types';
import { isEmpty } from 'lodash';

import { IWebBannerFields } from '../../../types/contentful';

import { usePublicRuntimeConfig } from '../../hooks/usePublicRuntimeConfig';
import { trackWithFingerprint } from '../../utils/trackWithFingerprint';
import { WebRegUrls, envEnum } from '../../context/PageData';
import { ColorTokenProp } from '../../theme/tokens/color';

import RichText from '../RichText';
import { EmbeddedInlineEntryNode } from '../RichText/Nodes';
import { Flexbox } from '../Flexbox';
import { Text } from '../Text';

import { ArrowNorthEast } from './ArrowNorthEast';

import {
  WebBannerWrapper,
  WebBannerButtonRight,
  WebBannerCloseButton,
  WebBannerButtonArrowOnly,
  InlineAnchor,
  CTALinkButtonRight,
  CTALinkButtonOnly
} from './styles';

import toggleClose from '@/images/toggle-close.svg';

export interface WebBannerProps extends IWebBannerFields {
  toggleBanner: Function;
}

export function WebBanner({
  title,
  titleColor,
  bannerBackgroundColor,
  message,
  messageColor,
  rightButtonText,
  rightButtonArrowColor,
  ctaUrl,
  previewCtaUrl,
  stagingCtaUrl,
  openInNewTab,
  toggleBanner
}: WebBannerProps) {
  const { nodeEnv } = usePublicRuntimeConfig();

  const handleCTAClick = () => {
    // Fire Segment Event
    trackWithFingerprint('CTA Clicked', {
      page_url: window.location.href,
      module: 'web banner'
    });
  };

  const target = openInNewTab ? '_blank' : '_self';

  const env = (nodeEnv as keyof typeof envEnum) || '';

  const envCtaUrls: WebRegUrls = {
    development: previewCtaUrl as string,
    preview: previewCtaUrl as string,
    stage: stagingCtaUrl as string,
    prod: ctaUrl as string
  };

  // Use CTA url based on env, if undefined don't show CTA button
  const url = envCtaUrls[envEnum[env]];

  return isEmpty(message) ? null : (
    <Flexbox
      backgroundColor="white"
      data-testid="webBanner"
      className="web-banner"
    >
      <WebBannerWrapper
        backgroundColor={(bannerBackgroundColor ?? 'black') as ColorTokenProp}
        borderRadius={{
          0: ['0', '0', 'pressPostHeader', 'pressPostHeader'],
          tablet: ['0', '0', 'pressPostHeader', 'pressPostHeader'],
          desktop: ['0', '0', 'pressPostHeader', 'pressPostHeader']
        }}
      >
        <Flexbox
          justifyContent="center"
          alignItems="center"
          marginLeft="ml"
          marginRight="ml"
        >
          <WebBannerCloseButton
            onClick={() => toggleBanner('hide')}
            data-testid="webBannerCloseButton"
          >
            <Image src={toggleClose} alt="close banner" />
          </WebBannerCloseButton>
        </Flexbox>
        <Flexbox flex={1} justifyContent="flex-start" alignItems="center">
          <RichText
            data={message}
            overrideOptions={{
              [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children) => (
                <Text
                  as="p"
                  fontWeight="p"
                  fontSize={{ 0: 's', tablet: 's', desktop: 'm' }}
                  textAlign="left"
                  style={{ color: messageColor ?? '#ffffff' }}
                  marginRight={{ 0: 'sm', tablet: 'ml', desktop: 'ml' }}
                >
                  <Text
                    as="span"
                    fontWeight="bold"
                    fontSize={{ 0: 's', tablet: 's', desktop: 'm' }}
                    textAlign="left"
                    style={{ color: titleColor ?? '#ffffff' }}
                    marginRight={{ 0: 's', tablet: 'ml', desktop: 'ml' }}
                  >
                    {title}
                  </Text>
                  {children}
                </Text>
              ),
              [INLINES.HYPERLINK]: (node, children) => (
                <InlineAnchor href={node.data.uri}>{children}</InlineAnchor>
              ),
              [INLINES.EMBEDDED_ENTRY]: (node, children) => (
                <EmbeddedInlineEntryNode
                  node={node}
                  colour="defaultText"
                  fontWeight="p"
                >
                  {children}
                </EmbeddedInlineEntryNode>
              )
            }}
          />
        </Flexbox>
        {url && (
          <Flexbox marginLeft="ml">
            <CTALinkButtonRight
              href={url}
              target={target}
              rel="noreferrer"
              aria-label={rightButtonText ?? ''}
              data-testid="webBannerCtaLink"
              attachParams={true}
            >
              <WebBannerButtonRight
                bgColor={bannerBackgroundColor}
                onClick={handleCTAClick}
                data-testid="webBannerCtaButton"
              >
                <Text
                  as="p"
                  fontWeight="p"
                  fontSize={{ 0: 's', desktop: 'sm' }}
                  textAlign="center"
                  colour="white"
                >
                  {rightButtonText}
                </Text>
                <ArrowNorthEast fill={rightButtonArrowColor ?? '#ffffff'} />
              </WebBannerButtonRight>
            </CTALinkButtonRight>
            <CTALinkButtonOnly
              href={url}
              target={target}
              rel="noreferrer"
              aria-label={rightButtonText ?? ''}
              attachParams={true}
            >
              <WebBannerButtonArrowOnly
                style={{ backgroundColor: bannerBackgroundColor ?? '' }}
                onClick={handleCTAClick}
              >
                <ArrowNorthEast fill={rightButtonArrowColor ?? '#ffffff'} />
              </WebBannerButtonArrowOnly>
            </CTALinkButtonOnly>
          </Flexbox>
        )}
      </WebBannerWrapper>
    </Flexbox>
  );
}
